import { API, graphqlOperation } from "aws-amplify";
import { updateTrackCar } from "../graphql/mutations";
import { getInboundShipmentTimePulled } from "../graphql/inbound/custom-queries";
import { getOutboundShipmentTimePulled } from "../graphql/outbound/custom-queries";
import { batchMutations, logError } from "./appUtils";
import { ResourceType, UNSPOTTED_TRACK_ID } from "./constants";
import { sortTrackCars, isSpotted } from "./trackListUtils";
import { buildTimePulledNote } from "./utils";
/** True if shipment is outbound (by parentType). */
export const isOutbound = (shipment) =>
  shipment?.parentType === ResourceType.OUTBOUND;

/**
 * Add the "Time Pulled" history note to a Track Management pull.
 *
 * A pull saves a minimal payload that carries no history, so the note the
 * Dispatch tab appends has to be read in first. History stays out of the
 * update unless the read came back whole: sending this one note alone would
 * wipe the shipment's history. A failed read costs the note, never the pull.
 *
 * The note goes on the embedded history array, not a HistoryNote row:
 * HistoryTab shows rows or the array, never both, and shipments have only
 * ever used the array, so a first row would hide all of it.
 *
 * @param {object} update - shipment update with id, parentType, collectedDateTime
 * @returns {Promise<object>} the update, with history when it could be read
 */
export const withTimePulledHistory = async (update) => {
  if (!update?.id) return update;
  const outbound = isOutbound(update);
  const query = outbound
    ? getOutboundShipmentTimePulled
    : getInboundShipmentTimePulled;
  const field = outbound ? "getOutboundShipment" : "getInboundShipment";

  let shipment = null;
  try {
    const res = await API.graphql(graphqlOperation(query, { id: update.id }));
    shipment = res?.data?.[field] || null;
  } catch (error) {
    // Amplify rejects any response carrying errors; the record may still be
    // on error.data.
    shipment = error?.data?.[field] || null;
    logError("utils:trackCarPersistence:withTimePulledHistory", error, false);
  }

  if (!Array.isArray(shipment?.history)) return update;
  const note = buildTimePulledNote(shipment, update);
  if (!note) return update;
  return { ...update, history: [...shipment.history, note] };
};

/**
 * Converts Date objects to ISO strings for GraphQL compatibility.
 * Only converts actual Date objects, leaves strings and other types unchanged.
 *
 * @param {*} value - Value to convert
 * @returns {string|*} ISO string if Date, otherwise original value
 */
const convertDateToISO = (value) => {
  if (value instanceof Date) {
    return value.toISOString();
  }
  return value;
};

const createMissingCompositeKeyFieldsError = ({
  shipmentId,
  keyName,
  flowTag,
  missingStatus,
  missingDateFields,
}) => {
  const details = {
    shipmentId: shipmentId || null,
    keyName,
    flowTag,
    missingStatus,
    missingDateFields: missingDateFields || [],
  };
  const error = new Error(
    `Missing required composite key fields for ${keyName}.`,
  );
  error.name = "MissingCompositeKeyFieldsError";
  error.code = "MISSING_COMPOSITE_KEY_FIELDS";
  error.details = details;
  return error;
};

/**
 * Ensures all composite sort key fields are included when updating shipments.
 * DynamoDB requires ALL fields of a composite sort key to be provided when updating any part.
 * Only includes fields that exist (not undefined/null) to avoid GraphQL validation errors.
 * Converts Date objects to ISO strings for GraphQL compatibility.
 *
 * @param {Object} shipment - Shipment object to ensure composite key fields for
 * @param {boolean} isOutbound - Whether this is an outbound shipment
 * @returns {Object} Shipment with all required composite key fields included
 */
export const ensureCompositeKeyFields = (shipment, isOutbound) => {
  if (!shipment) return shipment;

  const result = { ...shipment };

  // Helper to safely include a field only if it exists (not undefined/null)
  const includeField = (fieldName) => {
    const value = shipment[fieldName];
    if (value !== undefined && value !== null) {
      result[fieldName] = convertDateToISO(value);
    }
  };

  if (isOutbound) {
    // Outbound composite keys:
    // 1. 'outboundShipmentByStatusByActive' requires: status, active
    // 2. 'outboundShipmentByAccountByStatusByDates' requires: status, endDateTime, arrivalDateTime, scheduledDateTime, createDateTime

    const isUpdatingStatus = shipment.status !== undefined;
    const isUpdatingActive = shipment.active !== undefined;
    const isUpdatingDateFields =
      shipment.endDateTime !== undefined ||
      shipment.arrivalDateTime !== undefined ||
      shipment.scheduledDateTime !== undefined ||
      shipment.createDateTime !== undefined;

    if (isUpdatingStatus || isUpdatingActive || isUpdatingDateFields) {
      const isUpdatingStatusByActiveKey = isUpdatingStatus || isUpdatingActive;
      const isUpdatingStatusByDatesKey =
        isUpdatingStatus || isUpdatingDateFields;
      const outboundCompositeDateFields = [
        "endDateTime",
        "arrivalDateTime",
        "scheduledDateTime",
        "createDateTime",
      ];

      // Validate outbound status+dates key BEFORE adding any composite key fields.
      if (isUpdatingStatusByDatesKey) {
        const hasStatusField =
          Object.prototype.hasOwnProperty.call(shipment, "status") &&
          shipment.status !== undefined &&
          shipment.status !== null;
        const missingDateFields = outboundCompositeDateFields.filter(
          (fieldName) =>
            !Object.prototype.hasOwnProperty.call(shipment, fieldName),
        );

        if (!hasStatusField || missingDateFields.length > 0) {
          const details = {
            shipmentId: shipment?.id || null,
            missingStatus: !hasStatusField,
            missingDateFields,
            keyName: "outboundShipmentByAccountByStatusByDates",
            flowTag: "ensureCompositeKeyFields:outbound",
          };
          console.error(
            "ensureCompositeKeyFields:missingOutboundCompositeKeyFields",
            details,
          );
          throw createMissingCompositeKeyFieldsError({
            shipmentId: details.shipmentId,
            keyName: details.keyName,
            flowTag: details.flowTag,
            missingStatus: details.missingStatus,
            missingDateFields: details.missingDateFields,
          });
        }
      }

      // For 'outboundShipmentByStatusByActive' - if status or active is updated, include both (if they exist)
      if (isUpdatingStatusByActiveKey) {
        includeField("status");
        includeField("active");
      }

      // For 'outboundShipmentByAccountByStatusByDates' - if status or any date field is updated, include all required fields
      if (isUpdatingStatusByDatesKey) {
        result.status = convertDateToISO(shipment.status);
        outboundCompositeDateFields.forEach((fieldName) => {
          result[fieldName] = convertDateToISO(shipment[fieldName]);
        });
      }
    }
  } else {
    // Inbound composite key:
    // 'inboundShipmentByAccountByStatusByDates' requires: status, releaseDateTime, createDateTime, scheduledDateTime, notifyDateTime, placedDateTime, startDateTime, endDateTime
    // 'inboundShipmentByStateByRailcar' requires: state, railCarNumber

    const isUpdatingStatus = shipment.status !== undefined;
    const isUpdatingState = shipment.state !== undefined;
    const isUpdatingDateFields =
      shipment.releaseDateTime !== undefined ||
      shipment.createDateTime !== undefined ||
      shipment.scheduledDateTime !== undefined ||
      shipment.notifyDateTime !== undefined ||
      shipment.placedDateTime !== undefined ||
      shipment.startDateTime !== undefined ||
      shipment.endDateTime !== undefined;

    if (isUpdatingStatus || isUpdatingDateFields) {
      const hasStatusField =
        Object.prototype.hasOwnProperty.call(shipment, "status") &&
        shipment.status !== undefined &&
        shipment.status !== null;
      const inboundCompositeDateFields = [
        "releaseDateTime",
        "createDateTime",
        "scheduledDateTime",
        "notifyDateTime",
        "placedDateTime",
        "startDateTime",
        "endDateTime",
      ];
      const missingDateFields = inboundCompositeDateFields.filter(
        (fieldName) =>
          !Object.prototype.hasOwnProperty.call(shipment, fieldName),
      );

      if (!hasStatusField || missingDateFields.length > 0) {
        const details = {
          shipmentId: shipment?.id || null,
          missingStatus: !hasStatusField,
          missingDateFields,
          keyName: "inboundShipmentByAccountByStatusByDates",
          flowTag: "ensureCompositeKeyFields:inbound",
        };
        console.error(
          "ensureCompositeKeyFields:missingInboundCompositeKeyFields",
          details,
        );
        throw createMissingCompositeKeyFieldsError({
          shipmentId: details.shipmentId,
          keyName: details.keyName,
          flowTag: details.flowTag,
          missingStatus: details.missingStatus,
          missingDateFields: details.missingDateFields,
        });
      }

      includeField("status");
      inboundCompositeDateFields.forEach((fieldName) => {
        result[fieldName] = convertDateToISO(shipment[fieldName]);
      });
    }

    // Include state/railCarNumber pair when present on payload.
    if (isUpdatingState || shipment.railCarNumber !== undefined) {
      includeField("state");
      includeField("railCarNumber");
    }
  }

  return result;
};

/**
 * Removes TrackCar-related fields from shipment before saving.
 * These fields are UI-only and ordering is handled by TrackCar.sortKey.
 *
 * @param {Object} shipment - Shipment object to strip
 * @returns {Object} Shipment without TrackCar fields
 */
export const stripTrackCarFields = (shipment) => {
  if (!shipment) return shipment;
  const {
    trackCarId,
    trackCarSortKey,
    originalSortKey,
    accountColor, // Computed field from account.color, not a direct field
    productId, // Derived field for UI, not in shipment schema
    productName, // Derived field for UI, not in shipment schema
    productColor, // Derived field for UI, not in shipment schema

    eodSortKey,
    eodTrackId,
    trackId, // shipment-root only — never sent on shipment save; placement remains trackCar.trackId
    locationFullName,
    inbound,
    outbound,
    parentType,
    shipmentId,
    sortKey,

    ...rest
  } = shipment;

  // Remove nested objects that are not part of the mutation input (e.g., account, outbound/inbound, location objects)
  const sanitized = { ...rest };

  Object.keys(sanitized).forEach((key) => {
    const val = sanitized[key];
    const isDate = val instanceof Date;
    if (val && typeof val === "object" && !Array.isArray(val) && !isDate) {
      delete sanitized[key];
    }
  });

  return sanitized;
};

const getLocationByTrackId = (tracks, trackId) => {
  if (!tracks?.length || !trackId) return null;
  for (const t of tracks) {
    const loc = t.locations?.items?.[0];
    if (loc?.id === trackId) return loc;
  }
  return null;
};

/**
 * Persists track car updates using timestamp-based sortKeys.
 * NO LONGER REINDEXES - each car maintains its own sortKey.
 *
 * @param {string|Array<string>} trackIds - Single track ID or array of track IDs to update
 * @param {Object} processedCarsMap - Map of trackId -> array of cars
 * @param {Function} notify - Optional notification function for error messages
 * @param {Array} [tracks] - Optional tracks array to resolve areaId/zoneId/aisleId from Location
 * @param {Set|Array} [skipTrackCarIds] - Optional set of TrackCar IDs to skip
 * @returns {Promise<Object>} Result object with success flag and failed operations
 */
export const persistTrackCars = async (
  trackIds,
  processedCarsMap,
  notify,
  tracks = null,
  skipTrackCarIds = null,
) => {
  const operations = [];
  const trackIdsArray = Array.isArray(trackIds) ? trackIds : [trackIds];
  const skipSet = skipTrackCarIds instanceof Set ? skipTrackCarIds : new Set();

  trackIdsArray.forEach((trackId) => {
    const cars = processedCarsMap[trackId] || [];

    // CRITICAL: Skip TrackCars already moved in cross-track operations to avoid duplicates
    // CRITICAL: Update if trackId changed (cross-track move). SortKey-only changes are handled by performMovePersistence.
    cars.forEach((car) => {
      if (!car.id) return;
      if (skipSet.has(car.id)) return;

      const currentSortKey = car.sortKey ?? "";
      const trackIdChanged = car.trackId !== trackId;

      if (trackIdChanged) {
        operations.push(async () => {
          const resolvedActive = car.active === "false" ? "false" : "true";
          const resolvedEodTrackId = car.eodTrackId ?? trackId;
          const resolvedEodSortKey = car.eodSortKey ?? currentSortKey;
          const location = getLocationByTrackId(tracks, trackId);
          const input = {
            id: car.id,
            trackId,
            active: resolvedActive,
            sortKey: currentSortKey,
            eodTrackId: resolvedEodTrackId,
            eodSortKey: resolvedEodSortKey,
            locationFullName: car.locationFullName || "",
            ...(location?.areaId && { areaId: location.areaId }),
            ...(location?.zoneId && { zoneId: location.zoneId }),
            ...(location?.aisleId && { aisleId: location.aisleId }),
          };
          const result = await API.graphql(
            graphqlOperation(updateTrackCar, { input }),
          );
          return result;
        });
      }
    });
  });

  if (operations.length === 0) {
    return { success: true, failed: [] };
  }

  const results = await batchMutations(operations, {
    batchSize: 100,
    maxRetries: 3,
    retryDelay: 1000,
    batchDelay: 100,
  });

  const failed = results.filter((r) => !r.success);
  if (failed.length > 0) {
    const errorMsg = `Failed to update ${failed.length} of ${results.length} track cars`;
    logError(`persistTrackCars: ${errorMsg}`, { failed }, notify);
    if (notify) {
      notify(
        `${errorMsg}. Some changes may not have been saved. Please refresh and try again.`,
        "error",
      );
    }
  }

  return {
    success: failed.length === 0,
    failed: failed.map((f) => ({ index: f.index, error: f.error })),
  };
};

/**
 * Force persist track car updates (for rollback scenarios).
 * Used for rollback scenarios where we need to restore original state.
 *
 * @param {string|Array<string>} trackIds - Single track ID or array of track IDs to update
 * @param {Object} processedCarsMap - Map of trackId -> array of cars
 * @param {Function} notify - Optional notification function for error messages
 * @param {Array} [tracks] - Optional tracks array to resolve areaId/zoneId/aisleId from Location
 * @returns {Promise<Object>} Result object with success flag and failed operations
 */
export const forcePersistTrackCars = async (
  trackIds,
  processedCarsMap,
  notify,
  tracks = null,
) => {
  const operations = [];
  const trackIdsArray = Array.isArray(trackIds) ? trackIds : [trackIds];

  trackIdsArray.forEach((trackId) => {
    const cars = processedCarsMap[trackId] || [];

    // Force update all cars (for rollback scenarios)
    cars.forEach((car) => {
      if (!car.id) return;

      const currentSortKey = car.sortKey ?? "";

      // Always update if sortKey exists (for rollback scenarios)
      if (currentSortKey) {
        const resolvedEodTrackId = car.eodTrackId ?? trackId;
        const resolvedEodSortKey = car.eodSortKey ?? currentSortKey;
        const location = getLocationByTrackId(tracks, trackId);
        const input = {
          id: car.id,
          trackId,
          active: car.active === "false" ? "false" : "true",
          sortKey: currentSortKey,
          eodTrackId: resolvedEodTrackId,
          eodSortKey: resolvedEodSortKey,
          locationFullName: car.locationFullName || "",
          ...(location?.areaId && { areaId: location.areaId }),
          ...(location?.zoneId && { zoneId: location.zoneId }),
          ...(location?.aisleId && { aisleId: location.aisleId }),
        };
        operations.push(() =>
          API.graphql(graphqlOperation(updateTrackCar, { input })),
        );
      }
    });
  });

  if (operations.length === 0) {
    return { success: true, failed: [] };
  }

  const results = await batchMutations(operations, {
    batchSize: 100,
    maxRetries: 3,
    retryDelay: 1000,
    batchDelay: 100,
  });

  const failed = results.filter((r) => !r.success);
  if (failed.length > 0) {
    const errorMsg = `Failed to force update ${failed.length} of ${results.length} track cars`;
    logError(`forcePersistTrackCars: ${errorMsg}`, { failed }, notify);
    if (notify) {
      notify(
        `${errorMsg}. Some changes may not have been saved. Please refresh and try again.`,
        "error",
      );
    }
  }

  return {
    success: failed.length === 0,
    failed: failed.map((f) => ({ index: f.index, error: f.error })),
  };
};

/**
 * Canonical Location id for a TrackCar — i.e. the bucket key under which the
 * car lives in `processedCarsMap`.
 *
 * Invariant (enforced server-side, see ops audit 2026-04): every TrackCar
 * persists a `trackId` that is the Location id (or `UNSPOTTED_TRACK_ID` for
 * the virtual unspotted bucket). We deliberately do NOT consult
 * `trackCar.location.id`: lean subscription payloads carry a fresh `trackId`
 * with no `location` object, and the `{ ...existing, ...tc }` merge in
 * `processQueuedUpdates` would otherwise drag a stale cached `location`
 * forward and pin the car to its previous track after a move. For full
 * payloads `trackId === location.id`, so trusting `trackId` is also correct
 * there.
 *
 * Historical note: an earlier era stored `trackId` as the parent Aisle id
 * and used `location.id` to identify the specific slot. Those rows have been
 * backfilled; if they ever return, the symptom is cars vanishing from the
 * UI under an unknown bucket key, and the fix is a database backfill, not
 * resurrecting client-side aisle->location mapping.
 */
export const resolveTrackCarTrackId = (trackCar) => trackCar?.trackId ?? "";

/**
 * Maps raw TrackCar records into per-track lists of TrackCar records.
 * Sorts each list by sortKey.
 *
 * @param {Array} trackCars - Raw TrackCar records from GraphQL
 * @param {Object} options
 * @param {boolean} options.skipSort - If true, do not sort per-track lists (for fast-first-paint).
 * @returns {{ byTrackId: Object, placedShipmentIds: Set }}
 */
export const mapTrackCarsByTrack = (
  trackCars = [],
  { skipSort = false } = {},
) => {
  const byTrackId = {};
  const placedShipmentIds = new Set();

  // STAFF LEVEL FIX: Deduplicate track cars by shipmentId.
  // If multiple active track car records exist for the same shipment,
  // prioritize the one that has a trackId, then the one with the latest updatedAt.
  const dedupedMap = new Map();
  trackCars.forEach((tc) => {
    if (!tc || !tc.shipmentId) return;
    const existing = dedupedMap.get(tc.shipmentId);
    if (existing) {
      // 1. Prioritize spotted records (active=true on a physical track)
      const tcHasTrack = isSpotted(tc);
      const existingHasTrack = isSpotted(existing);

      if (existingHasTrack && !tcHasTrack) return;
      if (tcHasTrack && !existingHasTrack) {
        dedupedMap.set(tc.shipmentId, tc);
        return;
      }
      // 2. If both have trackId or both don't, prioritize latest updatedAt
      const existingTime = new Date(existing.updatedAt || 0).getTime();
      const tcTime = new Date(tc.updatedAt || 0).getTime();
      if (tcTime > existingTime) {
        dedupedMap.set(tc.shipmentId, tc);
      }
    } else {
      dedupedMap.set(tc.shipmentId, tc);
    }
  });

  const dedupedTrackCars = Array.from(dedupedMap.values());

  for (let i = 0; i < dedupedTrackCars.length; i++) {
    const trackCar = dedupedTrackCars[i];
    if (!trackCar || !trackCar.id) continue;

    const isUnspottedTrack = trackCar.trackId === UNSPOTTED_TRACK_ID;
    const isPlaced = isSpotted(trackCar);
    const isUnspotted = trackCar.active === "true" && isUnspottedTrack;

    if (isPlaced) {
      const placementTrackId = trackCar.trackId;

      placedShipmentIds.add(trackCar.shipmentId || trackCar.id);

      if (!byTrackId[placementTrackId]) {
        byTrackId[placementTrackId] = [];
      }
      byTrackId[placementTrackId].push(trackCar);
    } else if (isUnspotted) {
      if (!byTrackId[UNSPOTTED_TRACK_ID]) {
        byTrackId[UNSPOTTED_TRACK_ID] = [];
      }
      byTrackId[UNSPOTTED_TRACK_ID].push({
        ...trackCar,
        trackId: UNSPOTTED_TRACK_ID,
      });
    }
  }

  if (!skipSort) {
    const trackIds = Object.keys(byTrackId);
    for (let i = 0; i < trackIds.length; i++) {
      const trackId = trackIds[i];
      byTrackId[trackId] = sortTrackCars(byTrackId[trackId]);
    }
  }

  return { byTrackId, placedShipmentIds };
};
